import { ModuleApiProvider, useAuth } from '@berky/auth'
import type { HomeWidgetComponent, ModuleConfig } from '@berky/module-contract'
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  ErrorState,
  ModuleIcon,
  PageHeader,
  Skeleton,
  StatusBadge,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
  toast,
} from '@berky/ui'
import { ArrowRight, ChevronDown, MessageSquareText, Megaphone, Star } from 'lucide-react'
import { lazy, Suspense, useState, type ReactNode } from 'react'
import { Link } from 'react-router'

import { FeedbackStatusBadge } from '../feedback/management/components/feedback-status'
import { useMyFeedback } from '../feedback/my-feedback-api'
import { UnreadBadge } from '../feedback/unread-badge'
import { ChangeDetailDialog } from '../home/change-detail-dialog'
import { formatChangeDate, useChangeFeed, useNewChangeEntries } from '../home/change-feed'
import { homePanelStorageKey, useRememberedDisclosure } from '../home/panel-state'
import { ModuleErrorBoundary } from '../layout/module-error-boundary'
import { moduleAccess } from '../module-access'
import { moduleReferences, modules } from '../modules'
import {
  useFavorites,
  useModuleNavigation,
  useNavigationCategories,
  useSaveFavorites,
} from '../navigation/api'
import { findCategoryAssignment, type NavigationCategory } from '../navigation/types'
import { HomeLoading } from '../home/home-loading'

const MAX_FAVORITES = 5

/** Startseite als persönlicher Einstieg in Shell, Module und Plattformdialog. */
export function HomePage() {
  const { user } = useAuth()
  const navigation = useModuleNavigation()
  const categories = useNavigationCategories()
  const favorites = useFavorites()
  const saveFavorites = useSaveFavorites()
  const settings = new Map(
    (navigation.data ?? []).map((setting) => [setting.moduleId, setting]),
  )
  const activeCategories = (categories.data ?? [])
    .filter((category) => category.isActive)
    .toSorted((left, right) => left.sortOrder - right.sortOrder)
  const visible = modules.filter(
    (module) =>
      moduleAccess(user?.roles, module) === 'erlaubt' &&
      settings.get(module.id)?.isVisible &&
      settings
        .get(module.id)
        ?.categories.some((assignment) =>
          activeCategories.some((category) => category.id === assignment.categoryId),
        ),
  )
  const groups = activeCategories
    .map((category) => ({
      category,
      modules: visible
        .filter((module) => findCategoryAssignment(settings.get(module.id), category.id))
        .sort(
          (left, right) =>
            (findCategoryAssignment(settings.get(left.id), category.id)?.sortOrder ??
              Number.MAX_SAFE_INTEGER) -
              (findCategoryAssignment(settings.get(right.id), category.id)?.sortOrder ??
                Number.MAX_SAFE_INTEGER) || left.title.localeCompare(right.title, 'de'),
        ),
    }))
    .filter((group) => group.modules.length > 0)
  const favoriteModules = (favorites.data?.moduleIds ?? [])
    .map((id) => visible.find((module) => module.id === id))
    .filter((module): module is ModuleConfig => module !== undefined)

  async function toggleFavorite(moduleId: string) {
    const current = favoriteModules.map((module) => module.id)
    const next = current.includes(moduleId)
      ? current.filter((id) => id !== moduleId)
      : [...current, moduleId]
    try {
      await saveFavorites.mutateAsync(next)
      toast.success(
        current.includes(moduleId)
          ? 'Aus Schnellzugriff entfernt'
          : 'Zum Schnellzugriff hinzugefügt',
      )
    } catch {
      toast.error('Schnellzugriff konnte nicht gespeichert werden')
    }
  }

  if (navigation.isPending || categories.isPending) {
    return <HomeLoading />
  }
  if (navigation.isError || categories.isError || favorites.isError) {
    return (
      <ErrorState
        description="Ihre Anwendungen konnten nicht vollständig geladen werden."
        onRetry={() => {
          void navigation.refetch()
          void categories.refetch()
          void favorites.refetch()
        }}
      />
    )
  }

  return (
    <>
      <PageHeader
        className="pb-7"
        title={
          user
            ? `Guten Tag, ${user.name.split(' ')[0]}.`
            : 'Willkommen bei Berky Digital.'
        }
        description={
          user ? 'Womit möchten Sie beginnen?' : 'Ihre Anwendungen im Überblick.'
        }
        actions={<QuickAccess modules={favoriteModules} />}
      />

      {visible.length === 0 ? (
        <EmptyState
          title="Für Sie ist noch keine Anwendung freigeschaltet"
          description="Wenden Sie sich an Ihre Führungskraft oder an die IT, wenn Sie hier etwas erwarten."
        />
      ) : (
        <div className="grid grid-cols-[minmax(0,1fr)] items-start gap-6 xl:grid-cols-[minmax(0,1fr)_26rem]">
          <section aria-labelledby="anwendungen" className="min-w-0">
            <h2 id="anwendungen" className="sr-only">
              Ihre Anwendungen
            </h2>
            <div className="space-y-3">
              {groups.map((group) => (
                <ModuleGroup
                  key={group.category.id}
                  category={group.category}
                  modules={group.modules}
                  favoriteIds={favoriteModules.map((module) => module.id)}
                  favoriteLimitReached={favoriteModules.length >= MAX_FAVORITES}
                  isSavingFavorite={
                    saveFavorites.isPending || favorites.isPending || favorites.isError
                  }
                  onToggleFavorite={toggleFavorite}
                />
              ))}
            </div>
          </section>

          <aside
            className="grid min-w-0 grid-cols-[minmax(0,1fr)] gap-4 sm:grid-cols-2 xl:grid-cols-1"
            aria-label="Aktuelles"
          >
            <ChangeFeedPanel
              key={`changes-${user?.id ?? 'anonymous'}`}
              userId={user?.id}
            />
            <MyFeedbackPanel
              key={`feedback-${user?.id ?? 'anonymous'}`}
              userId={user?.id}
            />
          </aside>
        </div>
      )}
    </>
  )
}

function QuickAccess({ modules: favoriteModules }: { modules: ModuleConfig[] }) {
  return (
    <section aria-labelledby="schnellzugriff" className="w-full sm:w-auto">
      <h2 id="schnellzugriff" className="mb-2 text-xs font-medium text-muted-foreground">
        Schnellzugriff
      </h2>
      {favoriteModules.length === 0 ? (
        <p className="rounded-lg border border-dashed px-4 py-3 text-xs text-muted-foreground">
          Markieren Sie bis zu fünf Anwendungen mit dem Stern.
        </p>
      ) : (
        <ul className="flex w-full max-w-full flex-wrap gap-2">
          {favoriteModules.map((module) => (
            <li key={module.id} className="w-full sm:w-auto">
              <Link
                to={`/${module.id}`}
                className="flex w-full min-h-14 items-center gap-3 rounded-lg border bg-card px-3 py-2 shadow-xs transition-colors hover:bg-accent focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring sm:min-w-52"
              >
                <span className="grid size-10 shrink-0 place-items-center rounded-md bg-primary text-primary-foreground">
                  <ModuleIcon name={module.icon} className="size-5" />
                </span>
                <span className="min-w-0 flex-1 line-clamp-2 text-sm font-medium break-words">
                  {module.title}
                </span>
                <Star
                  className="size-4 shrink-0 fill-current text-marker"
                  aria-hidden="true"
                />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

function ModuleGroup({
  category,
  modules: groupModules,
  favoriteIds,
  favoriteLimitReached,
  isSavingFavorite,
  onToggleFavorite,
}: {
  category: NavigationCategory
  modules: ModuleConfig[]
  favoriteIds: string[]
  favoriteLimitReached: boolean
  isSavingFavorite: boolean
  onToggleFavorite: (moduleId: string) => Promise<void>
}) {
  const color = /^#[0-9A-Fa-f]{6}$/.test(category.colorHex)
    ? category.colorHex
    : '#102757'

  return (
    <section
      aria-labelledby={`category-${category.id}`}
      className="overflow-hidden rounded-xl border border-l-4 px-4 py-5 sm:px-5"
      style={{
        borderLeftColor: color,
        backgroundColor: `color-mix(in srgb, ${color} 10%, transparent)`,
      }}
    >
      <div className="grid grid-cols-[minmax(0,1fr)] gap-5 md:grid-cols-[13rem_minmax(0,1fr)] md:items-center">
        <header className="relative min-w-0 md:pr-3">
          <span
            aria-hidden="true"
            className="absolute -top-6 left-0 text-7xl font-semibold tracking-tight text-foreground/5"
          >
            {category.markerText}
          </span>
          <h3
            id={`category-${category.id}`}
            className="relative text-xl font-semibold tracking-tight"
          >
            {category.title}
          </h3>
          <p className="relative mt-1 max-w-48 text-sm leading-relaxed text-muted-foreground">
            {category.description}
          </p>
        </header>

        <ul className="grid min-w-0 grid-cols-[minmax(0,1fr)] gap-2 lg:grid-cols-2">
          {groupModules.map((module, index) => (
            <li
              key={module.id}
              className={cn(
                groupModules.length % 2 === 1 &&
                  index === groupModules.length - 1 &&
                  'lg:col-span-2',
              )}
            >
              <ModuleTile
                module={module}
                isFavorite={favoriteIds.includes(module.id)}
                favoriteLimitReached={favoriteLimitReached}
                onToggleFavorite={() => onToggleFavorite(module.id)}
                isSavingFavorite={isSavingFavorite}
              />
            </li>
          ))}
        </ul>
      </div>
    </section>
  )
}

const homeWidgets: Record<string, HomeWidgetComponent | undefined> = Object.fromEntries(
  modules.flatMap((module) => {
    const widget = module.homeWidget
    return widget ? [[module.id, lazy(widget)] as const] : []
  }),
)

function ModuleTile({
  module,
  isFavorite,
  favoriteLimitReached,
  onToggleFavorite,
  isSavingFavorite,
}: {
  module: ModuleConfig
  isFavorite: boolean
  favoriteLimitReached: boolean
  onToggleFavorite: () => void
  isSavingFavorite: boolean
}) {
  const Widget = homeWidgets[module.id]

  return (
    <article className="relative min-h-20 min-w-0 rounded-lg border bg-card px-4 py-4 shadow-xs transition-colors hover:bg-accent/45 focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-ring">
      <div className="flex min-w-0 items-center gap-3 pr-9">
        <span className="grid size-12 shrink-0 place-items-center rounded-md bg-primary text-primary-foreground shadow-xs">
          <ModuleIcon name={module.icon} className="size-6" />
        </span>
        <div className="min-w-0 flex-1">
          <h4
            className="line-clamp-2 text-base leading-snug font-semibold break-words"
            title={module.title}
          >
            <Link
              to={`/${module.id}`}
              className="outline-none after:absolute after:inset-0 after:content-['']"
            >
              {module.title}
            </Link>
          </h4>
          <p
            className="mt-1 line-clamp-2 text-sm text-muted-foreground"
            title={module.description}
          >
            {module.description}
          </p>
        </div>
      </div>

      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute top-2.5 right-2.5 z-20 size-8"
            aria-label={
              isFavorite
                ? `${module.title} aus Schnellzugriff entfernen`
                : `${module.title} zum Schnellzugriff hinzufügen`
            }
            aria-pressed={isFavorite}
            disabled={isSavingFavorite || (!isFavorite && favoriteLimitReached)}
            onClick={onToggleFavorite}
          >
            <Star
              className={isFavorite ? 'size-4 fill-current text-marker' : 'size-4'}
              aria-hidden="true"
            />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          {!isFavorite && favoriteLimitReached
            ? 'Maximal fünf Schnellzugriffe'
            : isFavorite
              ? 'Aus Schnellzugriff entfernen'
              : 'Zum Schnellzugriff hinzufügen'}
        </TooltipContent>
      </Tooltip>

      {Widget && <LiveWidgetSlot module={module} Widget={Widget} />}
    </article>
  )
}

function LiveWidgetSlot({
  module,
  Widget,
}: {
  module: ModuleConfig
  Widget: HomeWidgetComponent
}) {
  const [available, setAvailable] = useState(false)

  return (
    <div className={cn('relative z-10 mt-3 border-t pt-3', !available && 'hidden')}>
      <ModuleApiProvider
        module={{
          id: module.id,
          apiBasePath: module.apiBasePath,
          availableModules: moduleReferences,
        }}
      >
        <ModuleErrorBoundary moduleId={module.id} moduleTitle={module.title} compact>
          <Suspense fallback={null}>
            <Widget onAvailabilityChange={setAvailable} />
          </Suspense>
        </ModuleErrorBoundary>
      </ModuleApiProvider>
    </div>
  )
}

function PanelLoading({ label, rows }: { label: string; rows: number }) {
  return (
    <div role="status" aria-label={label} className="divide-y">
      <span className="sr-only">{label}</span>
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} aria-hidden="true" className="space-y-2 py-4">
          <Skeleton className="h-4 w-4/5" />
          <Skeleton className="h-3 w-1/3" />
        </div>
      ))}
    </div>
  )
}

function ChangeFeedPanel({ userId }: { userId?: string }) {
  const query = useChangeFeed()
  const entries = query.data?.slice(0, 5) ?? []
  const [selectedEntry, setSelectedEntry] = useState<(typeof entries)[number] | null>(
    null,
  )
  const [expanded, setExpanded] = useRememberedDisclosure(
    homePanelStorageKey(userId, 'change-feed-expanded'),
  )
  const { newEntryIds, acknowledge } = useNewChangeEntries(
    entries,
    expanded,
    homePanelStorageKey(userId, 'change-feed-seen-through'),
  )
  const contentId = 'home-change-feed-content'

  return (
    <Card className="min-w-0 gap-0 py-0 shadow-xs">
      <CollapsiblePanelHeader
        expanded={expanded}
        contentId={contentId}
        headingLabel="Neues aus Berky Digital"
        badge={
          !expanded ? (
            <UnreadBadge
              count={newEntryIds.size}
              label={`${newEntryIds.size} neue ${newEntryIds.size === 1 ? 'Änderung' : 'Änderungen'}`}
            />
          ) : null
        }
        onToggle={() => {
          if (expanded) acknowledge()
          setExpanded((current) => !current)
        }}
      >
        <Megaphone className="size-4 shrink-0 text-primary" aria-hidden="true" />
        <span>
          Neues aus{' '}
          <span aria-hidden="true">
            <span className="font-semibold tracking-wide text-foreground">BERKY</span>{' '}
            <span className="font-normal text-muted-foreground">Digital</span>
          </span>
        </span>
      </CollapsiblePanelHeader>
      {expanded && (
        <CardContent id={contentId} className="px-5 py-2">
          {query.isPending ? (
            <PanelLoading label="Änderungen werden geladen" rows={5} />
          ) : query.isError ? (
            <p className="py-5 text-sm text-muted-foreground">
              Die Änderungsübersicht ist gerade nicht verfügbar.
            </p>
          ) : entries.length === 0 ? (
            <p className="py-5 text-sm text-muted-foreground">
              Noch keine Änderungen veröffentlicht.
            </p>
          ) : (
            <ol className="divide-y">
              {entries.map((entry) => {
                const unread = newEntryIds.has(entry.id)
                return (
                  <li key={entry.id}>
                    <button
                      type="button"
                      className={cn(
                        sidebarEntryClassName,
                        unread && 'bg-info-surface/60',
                      )}
                      onClick={() => setSelectedEntry(entry)}
                    >
                      <UnreadMarker unread={unread} />
                      <span className="min-w-0">
                        <span
                          className={cn(
                            'block truncate text-sm',
                            unread ? 'font-semibold' : 'font-medium',
                          )}
                        >
                          {entry.title}
                        </span>
                        <span className="mt-1 flex min-w-0 items-center gap-2">
                          <span className="truncate text-xs text-muted-foreground">
                            {entry.area}
                          </span>
                          {unread && <StatusBadge tone="info">Neu</StatusBadge>}
                        </span>
                      </span>
                      <span className="flex items-center gap-2">
                        <time
                          className="text-xs whitespace-nowrap text-muted-foreground"
                          dateTime={entry.publishedAt}
                        >
                          {formatChangeDate(entry.publishedAt)}
                        </time>
                        <ArrowRight
                          className="size-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5"
                          aria-hidden="true"
                        />
                      </span>
                    </button>
                  </li>
                )
              })}
            </ol>
          )}
        </CardContent>
      )}
      <ChangeDetailDialog
        entry={selectedEntry}
        onOpenChange={(open) => {
          if (!open) setSelectedEntry(null)
        }}
      />
    </Card>
  )
}

function MyFeedbackPanel({ userId }: { userId?: string }) {
  const query = useMyFeedback(5)
  const entries = (query.data?.items ?? []).toSorted(
    (left, right) => Number(right.unreadMessages > 0) - Number(left.unreadMessages > 0),
  )
  const [expanded, setExpanded] = useRememberedDisclosure(
    homePanelStorageKey(userId, 'feedback-expanded'),
  )
  const contentId = 'home-feedback-content'

  return (
    <Card className="min-w-0 gap-0 py-0 shadow-xs">
      <CollapsiblePanelHeader
        expanded={expanded}
        contentId={contentId}
        headingLabel="Mein Feedback"
        badge={!expanded ? <UnreadBadge count={query.data?.unreadCount ?? 0} /> : null}
        onToggle={() => setExpanded((current) => !current)}
      >
        <MessageSquareText className="size-4 shrink-0 text-primary" aria-hidden="true" />
        Mein Feedback
      </CollapsiblePanelHeader>
      {expanded && (
        <CardContent id={contentId} className="px-5 py-2">
          {query.isPending ? (
            <PanelLoading label="Feedback wird geladen" rows={2} />
          ) : query.isError ? (
            <p className="py-5 text-sm text-muted-foreground">
              Ihr Feedback ist gerade nicht verfügbar.
            </p>
          ) : entries.length === 0 ? (
            <p className="py-5 text-sm text-muted-foreground">
              Sie haben noch kein Feedback gesendet.
            </p>
          ) : (
            <ul className="divide-y">
              {entries.map((entry) => (
                <li key={entry.id}>
                  <Link
                    to={`/mein-feedback/${entry.id}`}
                    className={sidebarEntryClassName}
                  >
                    <UnreadMarker unread={entry.unreadMessages > 0} />
                    <span className="min-w-0">
                      <span
                        className={cn(
                          'line-clamp-2 text-sm leading-relaxed',
                          entry.unreadMessages > 0 ? 'font-semibold' : 'font-medium',
                        )}
                      >
                        {entry.text}
                      </span>
                      <span className="mt-2 flex items-center gap-2">
                        <FeedbackStatusBadge status={entry.status} />
                        {entry.leadingProgress && (
                          <span className="text-xs text-muted-foreground">
                            Führendes Feedback: {entry.leadingProgress.status}
                          </span>
                        )}
                        {entry.unreadMessages > 0 && (
                          <StatusBadge tone="info">
                            {entry.unreadMessages === 1
                              ? 'Neue Änderung'
                              : `${entry.unreadMessages} neue Änderungen`}
                          </StatusBadge>
                        )}
                      </span>
                    </span>
                    <ArrowRight
                      className="mt-0.5 size-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5"
                      aria-hidden="true"
                    />
                  </Link>
                </li>
              ))}
            </ul>
          )}
          <div className="border-t py-3">
            <Link
              to="/mein-feedback"
              className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            >
              Alle meine Feedbacks
              <ArrowRight className="size-4" aria-hidden="true" />
            </Link>
          </div>
        </CardContent>
      )}
    </Card>
  )
}

const sidebarEntryClassName =
  'group grid w-full cursor-pointer grid-cols-[0.625rem_minmax(0,1fr)_auto] items-start gap-3 rounded-md px-2 py-4 text-left transition-colors hover:bg-accent/60 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring'

function UnreadMarker({ unread }: { unread: boolean }) {
  return (
    <span
      className={cn(
        'mt-1.5 size-2.5 rounded-full',
        unread ? 'bg-marker' : 'bg-muted-foreground/30',
      )}
      aria-hidden="true"
      data-slot="unread-marker"
    />
  )
}

function CollapsiblePanelHeader({
  expanded,
  contentId,
  headingLabel,
  badge,
  onToggle,
  children,
}: {
  expanded: boolean
  contentId: string
  headingLabel: string
  badge: ReactNode
  onToggle: () => void
  children: ReactNode
}) {
  return (
    <CardHeader
      className={cn('gap-0 px-0 py-0 [.border-b]:pb-0', expanded && 'border-b')}
    >
      <CardTitle
        className={cn(
          'relative flex cursor-pointer items-center gap-2 rounded-t-xl px-5 py-3 transition-colors hover:bg-accent/60',
          !expanded && 'rounded-b-xl',
        )}
      >
        <button
          type="button"
          className="absolute inset-0 z-10 cursor-pointer rounded-[inherit] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          aria-expanded={expanded}
          aria-controls={contentId}
          aria-label={`${headingLabel} ${expanded ? 'zuklappen' : 'aufklappen'}`}
          onClick={onToggle}
        />
        <h2
          aria-label={headingLabel}
          className="flex min-w-0 items-center gap-2 text-base"
        >
          {children}
        </h2>
        {badge}
        <ChevronDown
          className={cn(
            'ml-auto size-4 shrink-0 text-muted-foreground transition-transform',
            !expanded && '-rotate-90',
          )}
          aria-hidden="true"
        />
      </CardTitle>
    </CardHeader>
  )
}
